import React from 'react'

import { Alert } from 'react-bootstrap'
import { ISupportSpinner } from '../../../../../../shared/Ajax'
import { Option } from '../../../../../../shared/fields/Options'

import { Columns } from '../../../../../../shared/EditableTable/Model'
import { ApplicationForm2Editable } from '../../../../EditableTable'

import { ApplicationModel } from '../../../../../../models/ApplicationModel'

import initialDataEligibleExpenseConfig from './EligibleExpenseConfig'
import { ApplicationEligibleExpenseModel } from '../../../../../../models/ApplicationEligibleExpenseModel'
import { GRANT_GUIDELINES_URL } from '../../../..'
import { TierYearlyLimitEligibilityModel } from '../../../../../../models/TierYearlyLimitEligibilityModel'
import { GrantProgramTierConfiguration } from '../../../../../../models/GrantProgramTierConfiguration'
import { ApplicationFetch } from '../../../../../../controllers/ApplicationFetch'
import { ValidateMinimumMarketingSpendEligibilityModel } from '../../../../../../models/ValidateMinimumMarketingSpendEligibilityModel'
import { MinimumMarketingSpendEligibilityModel } from '../../../../../../models/MinimumMarketingSpendEligibilityModel'
import { formatMoneyForDisplay } from '../../../../../../shared/Utility'

export interface Props {
    model: ApplicationModel
    preview?: boolean
    onChange: { (changes: Partial<ApplicationModel>, callback?: { (): any }): any }
    tierNo: number
    isRepBody?: boolean
    financialYears: Option<string>[]
    eligibleExpenseCategories: Option<string>[]
}

export interface GrantAmount {
    financialYear?: string
    total?: number
}

export interface State {
    loading: boolean
    isTableIncomplete: boolean
    isTableValid: boolean
    dynamicErrorCodes: string[] // display this when `isTableValid` is false
    tierYearlyLimit: TierYearlyLimitEligibilityModel
    tierConfig: GrantProgramTierConfiguration[]
    totalGrantAmountsSought: number[]
}

const C_ERR_CODE_MANDATORY_RULE = 'MANDATORY_RULE_VALIDATION_FAILED'
const C_ERR_CODE_PRIOR_GRANT_RULE = 'PRIOR_GRANT_RULE_VALIDATION_FAILED'
const C_ERR_CODE_TIER_YEARLY_LIMIT_ELIGIBILITY = 'TIER_YEARLY_LIMIT_ELIGIBILITY_FAILED'
const C_ERR_CODE_TIER_NO_OF_YEARS_LIMIT = 'TIER_NO_OF_YEARS_LIMIT_FAILED'
const C_ERR_CODE_SAMPLE = 'FREE_SAMPLES_EXPENDITURE_AMOUNT_REACHED'
const C_ERR_CODE_MINIMUMGRANT = 'MINIMUM_GRANT_VALIDATION_FAILED'
const C_ERR_CODE_MAXIMUMGRANT = 'MAXIMUM_GRANT_VALIDATION_FAILED'
const C_ERR_CODE_MINIMUMELIGIBLEEXPENDITURE = 'MINIMUM_ELIGIBLE_EXPENDITURE_FAILED'
const C_ERR_CODE_MAXIMUMELIGIBLEEXPENDITURE = 'MAXIMUM_ELIGIBLE_EXPENDITURE_FAILED'
const C_ERR_CODE_GRANT_EXPENDITURE = 'GRANT_AMOUNT_EXCEEDS_MAX_EXPENDITURE'

export class EligibleExpenseTable extends React.Component<Props, State> implements ISupportSpinner {
  editable: ApplicationForm2Editable
  refDisplayErrorTableIncomplete: HTMLDivElement
  refDisplayErrorTableInvalid: HTMLDivElement
  timerHandle:NodeJS.Timeout
  isSampleCategoryOverLimit: boolean
  FY1: string
  FY2: string
  applicationFetch = new ApplicationFetch()

  constructor(props: Props) {
    super(props)

    // Validation flags
    this.isSampleCategoryOverLimit = false

    this.state = {
      loading: true,
      isTableIncomplete: false, // Default to Complete (hide initial Error Msg, only show on Save+Next)
      isTableValid: true, // Default to Valid (hide initial Error Msg, only show on Save+Next)
      dynamicErrorCodes: [],
      tierYearlyLimit: null,
      tierConfig: [],
      totalGrantAmountsSought: [this.props.model.totalGrantAmountSoughtFY1, this.props.model.totalGrantAmountSoughtFY2],
    }
  }

  handleTotalGrantAmountChange = (name: string, value: string) => {
    const { onChange, financialYears } = this.props

    if (!(financialYears && financialYears.length === 2)) return

    if (name === financialYears[0].label) {
      const totalGrantAmountFY1 = this.toNumber(value)
      this.state.totalGrantAmountsSought[0] = totalGrantAmountFY1
      onChange({ totalGrantAmountSoughtFY1: totalGrantAmountFY1 })
    }
    if (name === financialYears[1].label) {
      const totalGrantAmountFY2 = this.toNumber(value)
      this.state.totalGrantAmountsSought[1] = totalGrantAmountFY2
      onChange({ totalGrantAmountSoughtFY2: totalGrantAmountFY2 })
    }
  }

  componentDidMount() {
    const {
      model,
      model: {
        abn, financialYearName,
      }, onChange,
    } = this.props

    const isRepresentativeBody = model.isPeakIndustryBody
    const appTier = parseInt(model.applicationTier, 10)

    if (model.eligibleExpenses.length > 0) {
      const newArr = model.eligibleExpenses.map((item) => {
        if (item.crmEligibleExpenditureCategoryID === '00000000-0000-0000-0000-000000000000') {
          delete item.crmEligibleExpenditureCategoryID // remove prop from object, rather than have 000...etc
        }
        return item
      })
      onChange({ eligibleExpenses: newArr })
    }
    // Get the Tier Yearly limit and eligiblity data
    this.applicationFetch.validateTierYearlyLimitEligibility(abn, financialYearName, isRepresentativeBody, appTier).then((data: TierYearlyLimitEligibilityModel) => {
      this.setState({ tierYearlyLimit: data })
    })
      .catch((error) => {
        console.error('was not able to fetch tier yearly limit', error)
      })
    // Get Grant Program Tier Config data
    this.applicationFetch.getGrantProgramTierConfig()
      .then((data: GrantProgramTierConfiguration[]) => {
        this.setState({ tierConfig: data })
      })
      .catch((error) => {
        console.error('was not able to fetch tier config', error)
      })

    // Set financial years name
    this.FY1 = financialYearName
    this.FY2 = (parseInt(financialYearName, 10) + 1).toString()

    // Get existing value from total grant amount to populate into table
    this.state.totalGrantAmountsSought[0] = model.totalGrantAmountSoughtFY1 === null ? 0 : model.totalGrantAmountSoughtFY1
    this.state.totalGrantAmountsSought[1] = model.totalGrantAmountSoughtFY2 === null ? 0 : model.totalGrantAmountSoughtFY2

    // ajax call moved to parent,simulate ajax call completed
    this.timerHandle = setTimeout(() => {
      this.setState({ loading: false })
      this.timerHandle = null
    }, 200)
  }

  componentWillUnmount() {
    if (this.timerHandle) {
      clearTimeout(this.timerHandle)
      this.timerHandle = null
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { model, onChange } = this.props

    if (prevProps.model.totalExpenditureFY1 !== model.totalExpenditureFY1) {
      onChange({})
    }

    // if-clause to protect against infinite loop!
    if (JSON.stringify(prevProps.model.eligibleExpenses) !== JSON.stringify(model.eligibleExpenses)
      || prevProps.model.totalGrantAmountSoughtFY1 !== model.totalGrantAmountSoughtFY1
      || prevProps.model.totalGrantAmountSoughtFY2 !== model.totalGrantAmountSoughtFY2) {
      // Validate everytime table changes;.
      this.localValidate() // no callback
    }
  }

  checkIsTableIncomplete(): boolean {
    const { model } = this.props

    if (model.eligibleExpenses.length > 0) {
      const fullyCompleteRowsArr = model.eligibleExpenses.filter(
        (rowObj) => {
          if (Object.keys(rowObj).length === 0) {
            return false
          } if (
            (!(rowObj.hasOwnProperty('crmEligibleExpenditureCategoryID'))
                        || (!(rowObj.hasOwnProperty('fyExpenditureAmount1')))
                        || (!(rowObj.hasOwnProperty('fyExpenditureAmount2'))))
                            || ((rowObj.crmEligibleExpenditureCategoryID === undefined)
                            || (rowObj.fyExpenditureAmount1 === undefined)
                            || (rowObj.fyExpenditureAmount1 === null)
                            || (rowObj.fyExpenditureAmount1.toString() === '')
                            || (rowObj.fyExpenditureAmount2 === undefined)
                            || (rowObj.fyExpenditureAmount2 === null)
                            || (rowObj.fyExpenditureAmount2.toString() === ''))
          ) {
            return false
          }
          return true // i.e. this return is for the Array.filter;
        },
      )

      if (fullyCompleteRowsArr.length === model.eligibleExpenses.length) return false // table is good, i.e. incomplete is false;
      return true // table is bad, i.e. incomplete is true;
    }
    // else Table is EMPTY (validated on Save+Next only).
  }

  validateTable = (): boolean => {
    const isTblIncomplete = this.checkIsTableIncomplete()
    if (isTblIncomplete) {
      this.setState({
        isTableIncomplete: true,
      }, () => {
        this.setState({ isTableValid: true }) // hide other Error Alert
        this.refDisplayErrorTableIncomplete.focus()
      })
      return false
    }

    // any rows are complete; empty table is handled elsewhere.
    this.setState({
      isTableIncomplete: false,
    })

    // all validations pass
    this.setState({ isTableValid: true })
    return true
  }

  localValidate = (): void => {
    this.validateTable()
  }

  lastCheckIsTableValid = (): boolean => {
    const result = this.validateTable()
    return result
  }

  validatePriorGrant() {
    const { model } = this.props

    // Rule does not apply for rep bodies
    if (model.isPeakIndustryBody) return true

    // If error loading consolidatedGrantHistory, then the applicant can't continue
    if (!model.consolidatedGrantHistory) return false

    const enteredDataForFinancialYear1 = model.totalExpenditureFY1 !== 0
    const enteredDataForFinancialYear2 = model.totalExpenditureFY2 !== 0

    // If the applicant is eligible to enter eligible expenses for both financial years (i.e. they have received 6 or less previous grants)
    // and they have entered eligible expenses for one or both financial years (i.e., 2025/26 and/or 2026/27), then the applicant can continue
    if (model.consolidatedGrantHistory.totalNumberOfGrants <= 6) {
      return enteredDataForFinancialYear1 || enteredDataForFinancialYear2
    }

    // If the applicant is eligible to enter eligible expenses for one financial year (i.e. they have received 7 previous grants)
    // and they have entered eligible expenses for one financial year (i.e., 2025/26 or 2026/27), then the applicant can continue
    return (enteredDataForFinancialYear1 && !enteredDataForFinancialYear2) || (enteredDataForFinancialYear2 && !enteredDataForFinancialYear1)
  }

  validateTierYearlyLimitEligibility() {
    const { tierYearlyLimit } = this.state
    return tierYearlyLimit?.isEligible
  }

  validateTierNumberOfYearsLimit() {
    const { model } = this.props
    const { tierYearlyLimit: { tierRemainingYears } } = this.state

    // If not eligible, let eligibility rule display error message, make this rule pass
    if (!this.validateTierYearlyLimitEligibility()) {
      return true
    }

    // If not tierRemainingYears set, no limits apply, make this rule pass
    if (tierRemainingYears === null) return true

    const enteredDataForFinancialYear1 = model.totalExpenditureFY1 > 0
    const enteredDataForFinancialYear2 = model.totalExpenditureFY2 > 0

    // If tierRemainingYears set to 1, then applicant can enter expenditures for only one FY
    if (tierRemainingYears === 1) {
      return (enteredDataForFinancialYear1 && !enteredDataForFinancialYear2) || (enteredDataForFinancialYear2 && !enteredDataForFinancialYear1)
    }

    // all other cases pass. e.g tierRemainingYears === 2 and entered for 2 FY
    return true
  }

  getTierConfigForFY(financialYear:string) {
    const { model: { isPeakIndustryBody, applicationTier } } = this.props
    const { tierConfig } = this.state
    const tierNo = parseInt(applicationTier, 10)

    const tierConfigFY = tierConfig.find(({ isRepBody, tier, grantYear }) => (grantYear === financialYear && isPeakIndustryBody ? isRepBody : tier === tierNo))
    return tierConfigFY
  }

  validateMinimumTotalGrantAmount() {
    const { model } = this.props

    // minimum not checked for repbody
    if (model.isPeakIndustryBody) {
      return true
    }

    // check tier configs exist
    const tierConfigFY1 = this.getTierConfigForFY(this.FY1)
    const tierConfigFY2 = this.getTierConfigForFY(this.FY2)

    if (!tierConfigFY1 || !tierConfigFY2) return false

    // check at least one grant amount entered
    const grantAmountFY1 = model.totalGrantAmountSoughtFY1 ?? 0
    const grantAmountFY2 = model.totalGrantAmountSoughtFY2 ?? 0

    if (grantAmountFY1 === 0 && grantAmountFY2 === 0) return false

    // check grant amount entered for all financial years for which expenses are entered
    const expenditureAmountFY1 = model.totalExpenditureFY1 ?? 0
    const expenditureAmountFY2 = model.totalExpenditureFY2 ?? 0

    if (expenditureAmountFY1 !== 0 && grantAmountFY1 === 0) return false
    if (expenditureAmountFY2 !== 0 && grantAmountFY2 === 0) return false

    // check the entered grant amount is greater than minimum grant amount
    if (grantAmountFY1 !== 0 && grantAmountFY1 < tierConfigFY1.minimumGrantAmount) return false
    if (grantAmountFY2 !== 0 && grantAmountFY2 < tierConfigFY2.minimumGrantAmount) return false

    // if all checks pass, return true
    return true
  }

  validateMaximumTotalGrantAmount() {
    const { model } = this.props

    // check tier configs exist
    const tierConfigFY1 = this.getTierConfigForFY(this.FY1)
    const tierConfigFY2 = this.getTierConfigForFY(this.FY2)

    if (!tierConfigFY1 || !tierConfigFY2) return false

    // check the entered grant amount is less than maximum grant amount
    const grantAmountFY1 = model.totalGrantAmountSoughtFY1 ?? 0
    const grantAmountFY2 = model.totalGrantAmountSoughtFY2 ?? 0

    if (grantAmountFY1 !== 0 && grantAmountFY1 > tierConfigFY1.maximumGrantAmount) return false
    if (grantAmountFY2 !== 0 && grantAmountFY2 > tierConfigFY2.maximumGrantAmount) return false

    // if all checks pass, return true
    return true
  }

  async valiateMinimumEligibleExpenditure(): Promise<boolean> {
    const {
      model: {
        financialYearName, isPeakIndustryBody, applicationTier, totalExpenditureFY1, totalExpenditureFY2,
      },
    } = this.props

    if (!(totalExpenditureFY1 > 0 || totalExpenditureFY2 > 0)) return false

    const fyExpenditures = []
    if (totalExpenditureFY1 > 0) {
      fyExpenditures.push({ financialYear: this.FY1, expenditureAmount: totalExpenditureFY1 })
    }
    if (totalExpenditureFY2 > 0) {
      fyExpenditures.push({ financialYear: this.FY2, expenditureAmount: totalExpenditureFY2 })
    }

    let isMinimumSpendEligible = false
    await this.applicationFetch.validateMinimumMarketingSpendEligibility({
      financialYearName,
      isRepresentativeBody: isPeakIndustryBody,
      appTier: parseInt(applicationTier, 10),
      financialYearExpenditures: fyExpenditures,
    } as ValidateMinimumMarketingSpendEligibilityModel)
      .then((data: MinimumMarketingSpendEligibilityModel) => {
        isMinimumSpendEligible = (data && data.isEligible)
      })
      .catch((error) => {
        console.error('was not able to validate minimum eligible expenditure', error)
      })
    return isMinimumSpendEligible
  }

  getMinimumEligibleExpenditureAmount() {
    const tierConfig = this.getTierConfigForFY(this.FY1)
    return tierConfig?.minimumMarketingSpend ?? 0
  }

  getMaximumEligibleExpenditureAmount() {
    const tierConfig = this.getTierConfigForFY(this.FY1)
    return ((tierConfig?.maximumGrantAmount ?? 0) * 2)
  }

  validateMaximumEligibleExpenditure() {
    const {
      model: {
        totalExpenditureFY1, totalExpenditureFY2,
      },
    } = this.props

    const tierConfigFY1 = this.getTierConfigForFY(this.FY1)
    const tierConfigFY2 = this.getTierConfigForFY(this.FY2)

    if (!tierConfigFY1 || !tierConfigFY2) return false

    if ((totalExpenditureFY1 > ((tierConfigFY1.maximumGrantAmount ?? 0) * 2))
        || (totalExpenditureFY2 > ((tierConfigFY2.maximumGrantAmount ?? 0) * 2))) {
      return false
    }

    return true
  }

  async validate(callback: { (res: boolean): any }) {
    const { model } = this.props
    const {
      isTableIncomplete, isTableValid,
    } = this.state

    // special case: Mandatory check only on Save+Next
    if (!(model.eligibleExpenses && model.eligibleExpenses.length > 0)) {
      this.setState({
        isTableValid: false,
        dynamicErrorCodes: [C_ERR_CODE_MANDATORY_RULE],
      }, () => {
        this.refDisplayErrorTableInvalid.focus()
      })
      callback && callback(false) // validation fails
      return
    }

    // Other cases: just prevent Save+Next to continue; error is already on screen.
    if (isTableIncomplete || !isTableValid) {
      this.refDisplayErrorTableInvalid.focus()
      callback && callback(false)
      return
    }

    const errorCodes: string[] = []
    if (this.isSampleCategoryOverLimit) {
      errorCodes.push(C_ERR_CODE_SAMPLE)
    }

    if (!this.validateTierYearlyLimitEligibility()) {
      errorCodes.push(C_ERR_CODE_TIER_YEARLY_LIMIT_ELIGIBILITY)
    }

    if (!this.validateTierNumberOfYearsLimit()) {
      errorCodes.push(C_ERR_CODE_TIER_NO_OF_YEARS_LIMIT)
    }

    if (!this.validatePriorGrant()) {
      errorCodes.push(C_ERR_CODE_PRIOR_GRANT_RULE)
    }

    if (!this.validateMinimumTotalGrantAmount()) {
      errorCodes.push(C_ERR_CODE_MINIMUMGRANT)
    }

    if (!this.validateMaximumTotalGrantAmount()) {
      errorCodes.push(C_ERR_CODE_MAXIMUMGRANT)
    }

    if (!(await this.valiateMinimumEligibleExpenditure())) {
      errorCodes.push(C_ERR_CODE_MINIMUMELIGIBLEEXPENDITURE)
    }

    if (!this.validateMaximumEligibleExpenditure()) {
      errorCodes.push(C_ERR_CODE_MAXIMUMELIGIBLEEXPENDITURE)
    }

    // Since minimum grant amount is already checked above,
    // check only if total grant amount is greater than half of the total expenditure
    if (model.totalGrantAmountSoughtFY1 > (model.totalExpenditureFY1 / 2) || model.totalGrantAmountSoughtFY2 > (model.totalExpenditureFY2 / 2)) {
      errorCodes.push(C_ERR_CODE_GRANT_EXPENDITURE)
    }

    if (errorCodes.length > 0) {
      this.setState({
        isTableValid: false,
        dynamicErrorCodes: errorCodes,
      }, () => {
        this.refDisplayErrorTableInvalid.focus()
      })
      callback && callback(false) // validation fails
      return
    }

    /* special scenario: Error is NOT on screen, but we must still re-calc the state AT THE LAST MINUTE, just prior to continuing;
          /*This is because even if the error is already on screen, but user clicked away to another tab(1,2) or on the popup (Add/Edit of PlannedExpenditure section)
          , we would be reloading the Tab3 again WITHOUT an error displayed
          */
    if (!this.lastCheckIsTableValid()) {
      callback && callback(false)
      return
    }

    callback && callback(true)
  }

  // For Calc table totals
  toNumber = (anything: any) => {
    const tempNumber = Number(anything)
    return isNaN(tempNumber) ? 0 : tempNumber
  }

  // Calculate total on column for amount entered for each category selected (FY1)
  calcTableTotalExpenditureFY1 = (tableAsArray: ApplicationEligibleExpenseModel[]) => {
    const total = tableAsArray.reduce((accum, rowItem) => {
      const updatedSum = this.toNumber(rowItem.fyExpenditureAmount1) + accum
      return updatedSum
    }, 0)
    return total
  }

  calcTableTotalExpenditureFY2 = (tableAsArray: ApplicationEligibleExpenseModel[]) => {
    const total = tableAsArray.reduce((accum, rowItem) => {
      const updatedSum = this.toNumber(rowItem.fyExpenditureAmount2) + accum
      return updatedSum
    }, 0)
    return total
  }

  validateSampleCategory = (tableAsArray: ApplicationEligibleExpenseModel[]) => {
    const { eligibleExpenseCategories } = this.props
    const freeSampleCategory = eligibleExpenseCategories.find(({ label }) => label === 'Free samples')

    let isOverLimit : boolean = false
    for (let i = 0; i < tableAsArray.length; i++) {
      if (
        tableAsArray[i].crmEligibleExpenditureCategoryID === freeSampleCategory.value
            && (tableAsArray[i].fyExpenditureAmount1 > 30000 || tableAsArray[i].fyExpenditureAmount2 > 30000)
      ) {
        isOverLimit = true
        break
      }
    }
    return isOverLimit
  }

  toggleSpinner(on: boolean, callback: { (): any }): void {
    this.setState({ loading: on }, callback)
  }

  getInitData(): { columns: Columns[], data: any, skipReset: boolean, model: ApplicationModel, modelPath: string, propertyName: string } {
    const { model, financialYears, eligibleExpenseCategories } = this.props
    // modelPath: if the Tbl data was nested in the model, we'd use 'Prop1.Prop2.PropTblname'
    const tempVal = {
      ...initialDataEligibleExpenseConfig(eligibleExpenseCategories, financialYears, this.state.totalGrantAmountsSought, this.props.preview), data: model.eligibleExpenses, model, modelPath: 'eligibleExpenses', propertyName: 'eligibleExpenses',
    }
    return tempVal
  }

  // fires whenever new row is added.
  setTableIsEmptyFlagToFalse = () => {
    this.setState({
      isTableIncomplete: false,
    })
  }

  checkAllTableRowsAreNonZero() {
    const { model } = this.props
    let result = true
    const nonZeroArr = model.eligibleExpenses.filter((row) => row.fyExpenditureAmount1 > 0 || row.fyExpenditureAmount2 > 0)
    if (nonZeroArr.length !== model.eligibleExpenses.length) { result = false }

    return result
  }

  getSelectedCategories(): string[] {
    const { model: { eligibleExpenses } } = this.props
    const tempVal = eligibleExpenses.map((f) => f.crmEligibleExpenditureCategoryID)
    return tempVal
  }

  localOnChange = (changes: Partial<ApplicationModel>, callback?: { (): any }) => {
    const { onChange, model, financialYears } = this.props

    let totExpenseEarningFY1 = 0
    let totExpenseEarningFY2 = 0

    if (changes.eligibleExpenses.length > 0) {
      totExpenseEarningFY1 = this.calcTableTotalExpenditureFY1(changes.eligibleExpenses)
      totExpenseEarningFY2 = this.calcTableTotalExpenditureFY2(changes.eligibleExpenses)
    } else {
      totExpenseEarningFY1 = 0
      totExpenseEarningFY2 = 0
    } // table is Empty

    this.isSampleCategoryOverLimit = this.validateSampleCategory(changes.eligibleExpenses)

    // inject missing fields for DB
    const newArr = changes.eligibleExpenses.map((row) => {
      row.eligibleExpenseID = 0 // SET ALL PK to 0, as they will be ADDITIONS; prior to saving, existing db recs will be deleted;
      row.applicationID = model.applicationID // the FK;
      row.expenditureYear1 = financialYears[0].label
      row.expenditureYear2 = financialYears[1].label
      row.crmPlannedExpenditureYearId1 = financialYears[0].value
      row.crmPlannedExpenditureYearId2 = financialYears[1].value
      if (row.fyExpenditureAmount1 === null || row.fyExpenditureAmount1 === undefined) { row.fyExpenditureAmount1 = 0 }
      if (row.fyExpenditureAmount2 === null || row.fyExpenditureAmount2 === undefined) { row.fyExpenditureAmount2 = 0 }
      return row
    })
    // refresh updated UI-table, including totals, to the `model` in the parent component;
    onChange({ eligibleExpenses: newArr, totalExpenditureFY1: totExpenseEarningFY1, totalExpenditureFY2: totExpenseEarningFY2 })
    // then componentDidUpdate of this child should run, because parent state has updated!
  }

  render() {
    const {
      isTableIncomplete, isTableValid, dynamicErrorCodes,
    } = this.state
    const { eligibleExpenseCategories, financialYears } = this.props
    return (
      <>
        {financialYears.length > 0 && eligibleExpenseCategories.length > 0 ? (
          <ApplicationForm2Editable
            ref={(r) => { this.editable = r }}
            initData={this.getInitData()}
            onChange={this.localOnChange}
            uniqueSelectSelection
            selectedCategories={this.getSelectedCategories()}
            updateIsTableEmptyToFalseHandler={this.setTableIsEmptyFlagToFalse}
            maximumRows={eligibleExpenseCategories.length}
            multipleFooter
            onFooter2ValueChange={this.handleTotalGrantAmountChange}
          />
        ) : null}
        {isTableIncomplete && (
        <div tabIndex={-1} ref={(r) => { this.refDisplayErrorTableIncomplete = r }} className="mt-2">
          <Alert className="emdg-page-alerts emdg-page-alerts--error">
            The table has incomplete details.
          </Alert>
        </div>
        )}

        {!isTableValid && (
        <div tabIndex={-1} ref={(r) => { this.refDisplayErrorTableInvalid = r }} className="mt-2">
            {dynamicErrorCodes.includes(C_ERR_CODE_MANDATORY_RULE) && (
            <Alert className="emdg-page-alerts emdg-page-alerts--error">
              <span>The table is mandatory.</span>
            </Alert>
            )}
            {dynamicErrorCodes.includes(C_ERR_CODE_PRIOR_GRANT_RULE) && (
            <Alert className="emdg-page-alerts emdg-page-alerts--error">
              <span>
                You can only enter eligible expenditure for one financial year for this application.
                {' '}
                Yearly limits apply from Round 4. See section 4.1.1.5 of the
                {' '}
                <a target="blank" href={`${GRANT_GUIDELINES_URL}`} rel="noreferrer">Grant Guidelines</a>
                {' '}
                for further information.
                {' '}
                To complete the table accurately, select the financial year that you would like to receive funding for and input your planned expenditure category and amounts accordingly.
                {' '}
                For the financial year that you are not able to receive funding input $0.
              </span>
            </Alert>
            )}
            {dynamicErrorCodes.includes(C_ERR_CODE_SAMPLE) && (
            <Alert className="emdg-page-alerts emdg-page-alerts--error">
              <span>Free Samples expense category cannot be greater than $30,000 per financial year.</span>
            </Alert>
            )}
            {dynamicErrorCodes.includes(C_ERR_CODE_TIER_YEARLY_LIMIT_ELIGIBILITY) && (
            <Alert className="emdg-page-alerts emdg-page-alerts--error">
              <span>
                {' '}
                You are
                {' '}
                <b>not</b>
                {' '}
                eligible to apply for a grant with Austrade for this tier.
                <br />
                Tier limits apply from Round 4. See section 4.1.1.5 of the
                {' '}
                <a href="https://www.austrade.gov.au/content/dam/austrade-assets/global/wip/austrade/documents/emdg-grant-guidelines-round-4.docx" target="_blank" rel="noreferrer">Grant Guidelines</a>
                {' '}
                for further information.
              </span>
            </Alert>
            )}
            {dynamicErrorCodes.includes(C_ERR_CODE_TIER_NO_OF_YEARS_LIMIT) && (
            <Alert className="emdg-page-alerts emdg-page-alerts--error">
              <span>
                You can only enter eligible expenditure for one financial year for this tier. Tier limits apply from Round 4.
                {' '}
                See section 4.1.1.5 of the
                {' '}
                <a href="https://www.austrade.gov.au/content/dam/austrade-assets/global/wip/austrade/documents/emdg-grant-guidelines-round-4.docx" target="_blank" rel="noreferrer">Grant Guidelines</a>
                {' '}
                for further information.
                {' '}
                To complete the table accurately, select the financial year that you would like to receive funding for and input your planned expenditure category and amounts accordingly.
                {' '}
                For the financial year that you are not able to receive funding input $0.
              </span>
            </Alert>
            )}
            {dynamicErrorCodes.includes(C_ERR_CODE_MINIMUMELIGIBLEEXPENDITURE) && (
            <Alert className="emdg-page-alerts emdg-page-alerts--error">
              <span>
                The planned expenditure for the financial year must be more than
                {` ${formatMoneyForDisplay(this.getMinimumEligibleExpenditureAmount())} `}
                which is the minimum eligible expenditure limit for your tier.
              </span>
            </Alert>
            )}
          {dynamicErrorCodes.includes(C_ERR_CODE_MAXIMUMELIGIBLEEXPENDITURE) && (
          <Alert className="emdg-page-alerts emdg-page-alerts--error">
            <span>
              The planned expenditure for the financial year must not be more than
              {` ${formatMoneyForDisplay(this.getMaximumEligibleExpenditureAmount())} `}
              which is the maximum eligible expenditure limit for your tier.
            </span>
          </Alert>
          )}
          {dynamicErrorCodes.includes(C_ERR_CODE_MINIMUMGRANT) && (
            <Alert className="emdg-page-alerts emdg-page-alerts--error">
              <span>
                The grant amount sought that you are requesting is less than the minimum grant amount available for this tier. Minimum and maximum grant amounts per tier apply from Round 4.
                {' '}
                See section 3.1.2 of the
                {' '}
                <a href="https://www.austrade.gov.au/content/dam/austrade-assets/global/wip/austrade/documents/emdg-grant-guidelines-round-4.docx" target="_blank" rel="noreferrer">Grant Guidelines</a>
                {' '}
                for further information.
                {' '}
                <br />
              </span>

            </Alert>
          )}
            {dynamicErrorCodes.includes(C_ERR_CODE_MAXIMUMGRANT) && (
            <Alert className="emdg-page-alerts emdg-page-alerts--error">
              <span>
                The grant amount sought that you are requesting is greater than the maximum grant amount available for this tier. Minimum and maximum grant amounts per tier apply from Round 4.
                {' '}
                See section 3.1.1 of the
                {' '}
                <a href="https://www.austrade.gov.au/content/dam/austrade-assets/global/wip/austrade/documents/emdg-grant-guidelines-round-4.docx" target="_blank" rel="noreferrer">Grant Guidelines</a>
                {' '}
                for further information.
                {' '}
                <br />
              </span>

            </Alert>
            )}
          {dynamicErrorCodes.includes(C_ERR_CODE_GRANT_EXPENDITURE) && (
          <Alert className="emdg-page-alerts emdg-page-alerts--error">
            <span>
              The grant amount sought that you are requesting cannot be greater than 50% of the total eligible expenditure amount that has been calculated.
            </span>
          </Alert>
          )}
        </div>
        )}
      </>
    )
  }
}
